import React, { createContext, useContext, useReducer } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Spacing } from "components/designV2/constants";
import { Toast } from "components/designV2/toast/toast";
import { ZIndex } from "config/zIndex";
import { toastActions } from "./toastStore.actions";
import {
  ToastActions,
  ToastState,
  initialState,
  toastStateReducer,
} from "./toastStore.reducer";

export type ToastDispatch = (action: ToastActions) => void;

const ToastStateContext = createContext<ToastState | undefined>(undefined);
const ToastDispatchContext = createContext<ToastDispatch | undefined>(
  undefined,
);

export const ToastStoreProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(toastStateReducer, initialState);

  return (
    <ToastStateContext.Provider value={state}>
      <ToastDispatchContext.Provider value={dispatch}>
        {children}
        {createPortal(
          <ToastList aria-live="polite">
            {state.toasts.map((toast) => (
              <Toast
                key={toast.id}
                variant={toast.variant}
                message={toast.message}
                title={toast.title}
                durationMs={toast.durationMs}
                onDismiss={() =>
                  dispatch({ type: "DISMISS_TOAST", payload: { id: toast.id } })
                }
              />
            ))}
          </ToastList>,
          document.body,
        )}
      </ToastDispatchContext.Provider>
    </ToastStateContext.Provider>
  );
};

export const useToastStore = () => {
  const state = useContext(ToastStateContext);
  const dispatch = useContext(ToastDispatchContext);

  if (state === undefined || dispatch === undefined) {
    throw new Error("useToastStore must be used within a ToastStoreProvider");
  }

  const actions = toastActions(dispatch);

  return [state, actions, dispatch] as const;
};

const ToastList = styled.div`
  position: fixed;
  top: ${Spacing.md}px;
  right: ${Spacing.md}px;
  z-index: ${ZIndex.toast};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  width: min(420px, calc(100vw - ${Spacing.md * 2}px));
  pointer-events: none;
`;
